import { Box, Button, Text } from '@shopify/polaris';
import { useInfiniteQuery } from '@tanstack/react-query';
import Timeline from './Timeline';
import axios from 'axios';
import { getActivityLogQueryOptions } from '#client/utils/queryOptions';

/**
 * ActivityTracker component that displays a paginated timeline of activity logs.
 * Fetches and displays audit trail events for any object type, with support for hierarchical parent-child relationships.
 * Takes in a series of mandatory props, with an optional purchaseOrder prop for use within the purchase order screens.
 */
export default function ActivityTracker({ objectType, objectId, parentObjectType, parentObjectId }) {
    const fetchLogs = async ({ pageParam }) => {
        if (!objectType) return [];
        const params = {
            objectType,
            skip: pageParam === 1 ? 0 : (pageParam - 2) * 100 + 10, // Skip 10 for the second page and an additional 100 / page thereafter
            take: pageParam === 1 ? 10 : 100,
        };

        if (objectId) params.objectId = objectId;
        if (parentObjectId) params.parentObjectId = parentObjectId;
        if (parentObjectType) params.parentObjectType = parentObjectType;

        const { data } = await axios.get(`${import.meta.env.VITE_API_URL}/app/activity-logs`, { params });
        if (!data.success) throw new Error(data.message || 'Failed to fetch logs');
        return data.data;
    };

    const { data, isLoading, isError, error, hasNextPage, fetchNextPage } = useInfiniteQuery({
        ...getActivityLogQueryOptions(objectType, objectId, parentObjectType, parentObjectId),
        queryFn: ({ pageParam }) => fetchLogs({ pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => (lastPage.hasMore ? pages.length + 1 : undefined),
        refetchOnMount: 'always', // this is needed so if the user navigates between pages without refreshing it will refetch data and not stay in a cached state
    });

    const logs = data?.pages.flatMap((page) => page.logs) ?? [];

    const actionMap = {
        CREATE: 'success',
        SCAN: 'scan',
        DELETE: 'delete',
        SUBMIT: 'submit',
    };

    // Map logs to Timeline format
    const timelineItems = logs.map((log) => ({
        tone: actionMap[log.action] || 'base',
        timelineEvent: log.message,
        timestamp: log.timestamp ? new Date(log.timestamp) : undefined,
        staffId: log.staffId,
        action: log.action,
        objectId: log.objectDetails?.netSuiteHumanPoNumber || log.objectId,
        objectType: log.objectType,
        parentObjectId: log.parentObjectId,
        name: log.objectDetails?.name,
    }));

    return (
        <Box paddingBlockEnd="600">
            <Box paddingBlockEnd="200">
                <Text variant="headingMd" as="h2">
                    Activity Log
                </Text>
            </Box>
            {isLoading && <div>Loading...</div>}
            {isError && <div style={{ color: 'red' }}>{error.message}</div>}
            {!isLoading && !isError && <Timeline items={timelineItems} />}
            {hasNextPage ? (
                <Box padding="200" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button onClick={fetchNextPage}>Load More</Button>
                </Box>
            ) : (
                <></>
            )}
        </Box>
    );
}
